// pages/AIWorkspace.js — Halaman AI mandiri (full page).
// Pilih dashboard/data yang mau dianalisis, atau mode "Umum" (tanpa data).
// Sesi chat TERPISAH dari panel AI di header. Permission: 'ai_workspace'.
import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import { ChevronDown, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { loadConfigFromServer, getDefaultConfig } from '../components/AI/aiConfig';
import AIChat, { GENERAL_STORAGE_LABEL, getDashboardContext } from '../components/AI/AIChat';
import AIHistorySidebar from '../components/AI/AIHistorySidebar';
import { toChatMessages } from '../components/AI/sessionResume';
import useHistorySidebarSync from '../components/AI/useHistorySidebarSync';
import { ROUTE_META } from '../routesMeta';   // label + permission per dashboard (anti circular import)

// Target "Umum" selalu ada (key kosong = mode tanpa data dashboard).
// Target dashboard lain dibangun DINAMIS dari daftar SQL-mode aktif (backend)
// → label & permission diambil dari ROUTE_META. Tambah dashboard AI = cukup
// daftarkan SQL Mode di AI Data Sources, tanpa mengubah file ini.
// label: HANYA fallback tampilan — teks dropdown sebenarnya dari t('aiw.general')
// lewat labelOf(). storageLabel: nama KANONIK yang ditulis ke ai_chat_history
// (lihat GENERAL_STORAGE_LABEL di AIChat.js). Keduanya sengaja berbeda: yang
// satu ikut bahasa UI, yang satu harus stabil lintas bahasa agar riwayat mode
// Umum tidak terbelah per locale.
// Lebar sidebar riwayat — sama dengan HISTORY_W di AIPanel agar konsisten.
const HISTORY_W = 260;

const GENERAL_TARGET = { key: '', label: 'Umum (tanpa data dashboard)', permission: null };
// Target "Coding" SUDAH TIDAK DI SINI — dipindah ke halaman sendiri
// (pages/AICoding.js, permission 'ai_coding'). Jangan tambahkan kembali:
// mode Coding tidak punya data dashboard dan gerbang izinnya berbeda.

export default function AIWorkspace() {
  const { hasAccess } = useAuth();
  const { t, lang }   = useLang();
  const location      = useLocation();
  const navigate      = useNavigate();

  const [config, setConfig]   = useState(null);
  const [loading, setLoading] = useState(true);

  // Daftar target = "Umum" + dashboard SQL-mode aktif yang user punya izinnya.
  // ROUTE_META dipetakan per key untuk ambil label + permission.
  const [sqlKeys, setSqlKeys] = useState(null); // null = belum termuat
  const [mrTargets, setMrTargets] = useState([]); // [MR-PG-AI] [{key,label}]
  const metaByKey = React.useMemo(() => {
    const m = {};
    ROUTE_META.forEach(r => { m[r.key] = r; });
    return m;
  }, []);

  const targets = React.useMemo(() => {
    const dyn = (sqlKeys || [])
      .map(k => metaByKey[k])
      .filter(Boolean)                                   // key tak dikenal di ROUTE_META → dilewati
      .filter(r => !r.permission || hasAccess(r.permission))  // hasAccess menangani '__admin__' & admin
      .map(r => ({ key: r.key, label: r.label, permission: r.permission }));
    // [MR-PG-AI] Source dari database Management Reports (PostgreSQL) — daftar &
    // izinnya (mr_ai_data) ditentukan backend /api/ai/mr-sources.
    const mr = (mrTargets || []).map(s => ({ key: s.key, label: s.label, permission: null }));
    return [GENERAL_TARGET, ...dyn, ...mr];
  }, [sqlKeys, mrTargets, metaByKey, hasAccess]);

  const [sel, setSel] = useState(GENERAL_TARGET);

  // ── Sidebar riwayat ────────────────────────────────────────────────────────
  // Pola sama dgn AIPanel: `resume` hanya dibaca AIChat di initializer, jadi
  // memuat sesi WAJIB lewat remount paksa — itu guna `chatKey`. Sebelumnya key
  // di sini konstanta 'ai-workspace', yang membuat pilih-sesi tidak berefek.
  const [resume, setResume]                   = useState(null);
  const [chatKey, setChatKey]                 = useState('ai-workspace');
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [historyTick, setHistoryTick]         = useState(0);
  const [picking, setPicking]                 = useState(false);
  // Default TERTUTUP, dan saling meniadakan dengan sidebar navigasi aplikasi:
  // membuka riwayat melipat sidebar, membentangkan sidebar menutup riwayat.
  // Aturan lebar layar yang lama dibuang — lihat useHistorySidebarSync.js.
  const [showHistory, toggleHistory] = useHistorySidebarSync('ai_workspace');

  // ── [Auto-detect] Mode Umum: AIChat mendeteksi dashboard relevan lalu
  // memanggil ini untuk ganti dropdown "diam-diam" (giliran berikutnya) —
  // suppressResetRef mencegah riwayat chat terhapus akibat pergantian ini.
  const suppressResetRef = useRef(false);
  // Ref TERPISAH dari suppressResetRef. Jangan gabungkan: AIChat mengonsumsi DAN
  // mengosongkan suppressResetRef di effect-nya sendiri (AIChat.js), dan effect
  // anak berjalan SEBELUM effect induk — jadi saat effect di file ini membacanya
  // nilainya sudah false, dan sesi yang sedang berjalan akan ikut ter-reset tepat
  // saat router memindahkan target secara otomatis.
  const skipResetRef = useRef(false);
  const [autoPicked, setAutoPicked] = useState(false);
  const handleAutoDetect = React.useCallback((key) => {
    const match = targets.find(tgt => tgt.key === key);
    if (!match || match.key === sel.key) return;
    suppressResetRef.current = true;
    skipResetRef.current     = true;   // pergantian ini bukan tindakan user
    setAutoPicked(key !== ''); // reset ke General bukan "auto-selected", cuma balik netral
    setSel(match);
  }, [targets, sel.key]);
  const resolveDashboardLabel = React.useCallback((key) => {
    const found = targets.find(tgt => tgt.key === key);
    return found ? labelOf(found) : key;
  }, [targets]); // eslint-disable-line

  // Default picker dari ?src=<dashboardKey> (dikirim tab AI mobile saat ada
  // dashboard aktif). PENTING: AIWorkspace TIDAK unmount saat navigasi antar
  // halaman (DashboardContainer menyembunyikan via CSS, komponen tetap hidup)
  // — jadi guard "terapkan sekali lalu selesai" akan terkunci selamanya
  // setelah src PERTAMA berhasil diterapkan, mengabaikan src BERIKUTNYA saat
  // user pindah dashboard lain lalu buka AI lagi. Sebagai gantinya, lacak nilai
  // src TERAKHIR yang sudah diproses — begitu src di URL berbeda dari itu
  // (dashboard baru), coba terapkan lagi.
  const lastSrcRef = React.useRef(null);
  useEffect(() => {
    const src = new URLSearchParams(location.search).get('src') || '';
    if (src === lastSrcRef.current) return;      // src ini sudah diproses, jangan ulangi
    if (!src) { lastSrcRef.current = ''; return; }
    const match = targets.find(tgt => tgt.key === src);
    if (match) {
      setSel(match);
      lastSrcRef.current = src;                  // sukses diterapkan
    }
    // Belum match (mis. sqlKeys belum termuat) → JANGAN tandai selesai;
    // effect ini otomatis dicoba lagi saat `targets` berubah (dependency).
  }, [targets, location.search]);

  // Jaga agar `sel` tetap valid saat daftar target berubah (mis. setelah fetch).
  useEffect(() => {
    if (!targets.find(tgt => tgt.key === sel.key)) setSel(targets[0] || GENERAL_TARGET);
  }, [targets]); // eslint-disable-line

  // Label tampilan: mode umum (key kosong) diterjemahkan, label produk dibiarkan apa adanya.
  const labelOf = (tgt) => (tgt.key === '' ? t('aiw.general') : tgt.label);
  // Nama untuk PENYIMPANAN riwayat — jangan pakai labelOf() di sini.
  // Target ber-key aman karena AIChat memetakannya lewat DASH_HINTS/ROUTE_META
  // yang sudah statis; hanya mode Umum yang butuh sentinel eksplisit.
  const storageLabelOf = (tgt) => (tgt.key === '' ? GENERAL_STORAGE_LABEL : tgt.label);

  // Nama untuk memfilter sidebar. WAJIB lewat getDashboardContext() atas nilai
  // PENYIMPANAN (bukan labelOf), karena itulah yang benar-benar ditulis AIChat
  // ke kolom `dashboard`. Memakai label terjemahan di sini = sidebar kosong.
  const dashboardName = getDashboardContext(sel.key || null, storageLabelOf(sel)).name || '';

  const handleSessionActivity = React.useCallback((sid) => {
    setActiveSessionId(sid);
    setHistoryTick(n => n + 1);
  }, []);

  const handlePickSession = React.useCallback(async (sessionId) => {
    if (picking || sessionId === activeSessionId) return;
    setPicking(true);
    try {
      const r = await api.get(`/api/ai/chat-history/session/${encodeURIComponent(sessionId)}`);
      setResume({ session_id: sessionId, messages: toChatMessages(r.data, lang) });
      setChatKey(`ai-workspace-${sessionId}`);
      setActiveSessionId(sessionId);
    } catch (e) {
      console.error('[AIWorkspace] gagal memuat sesi:',
        e?.response?.status || '', e?.response?.data?.error || e.message);
      alert(`Gagal memuat sesi: ${e?.response?.data?.error || e.message}`);
    } finally {
      setPicking(false);
    }
  }, [picking, activeSessionId, lang]);

  // ── [DEEP LINK] ?session=<id> — dipakai "Open in new tab" dari sidebar riwayat
  // (halaman ini DAN panel AI di header, yang mendarat ke sini karena panel
  // bukan rute tersendiri).
  //
  // Pola guard-nya sama persis dengan `?src` di atas dan untuk alasan yang sama:
  // AIWorkspace TIDAK unmount saat pindah halaman, jadi guard "sekali lalu
  // selesai" akan terkunci selamanya setelah deep-link PERTAMA.
  //
  // Aman berdampingan dengan effect reset `sel.key`: link yang dibuat di sini
  // hanya membawa `session`, tidak pernah `src`, sehingga target tidak berubah
  // dan reset tidak terpicu. Kalau suatu saat keduanya digabung dalam satu URL,
  // urutannya harus ditinjau ulang — reset akan menghapus sesi yang baru dimuat.
  const lastDeepSidRef = useRef(null);
  useEffect(() => {
    const sid = new URLSearchParams(location.search).get('session') || '';
    if (!sid || sid === lastDeepSidRef.current) return;
    lastDeepSidRef.current = sid;
    handlePickSession(sid);
  }, [location.search, handlePickSession]);

  const handleNewChat = React.useCallback(() => {
    setResume(null);
    // Key unik, bukan konstanta — kalau nilainya kebetulan sama, React tidak
    // me-remount dan transkrip lama tetap di layar.
    setChatKey(`ai-workspace-new-${Date.now()}`);
    setActiveSessionId(null);
  }, []);

  // Ganti target lewat DROPDOWN = mulai bersih. Tanpa ini, sesi yang sudah
  // tersimpan di bawah nama dashboard lama akan dilanjutkan di target baru, dan
  // satu percakapan terbelah jadi dua nama di ai_chat_history. Pergantian dari
  // auto-detect dikecualikan (skipResetRef) karena itu bukan tindakan user.
  const firstRunRef = useRef(true);
  useEffect(() => {
    // Mount pertama bukan "ganti target" — tanpa guard ini AIChat ter-remount
    // sekali sia-sia tepat setelah render pertama.
    if (firstRunRef.current) { firstRunRef.current = false; return; }
    if (skipResetRef.current) { skipResetRef.current = false; return; }
    setResume(null);
    setActiveSessionId(null);
    setChatKey(`ai-workspace-${sel.key || 'general'}-${Date.now()}`);
  }, [sel.key]);

  useEffect(() => {
    loadConfigFromServer()
      .then(cfg => setConfig(cfg || getDefaultConfig()))
      .catch(() => setConfig(getDefaultConfig()))
      .finally(() => setLoading(false));
  }, []);

  // Muat daftar dashboard SQL-mode aktif (dinamis).
  useEffect(() => {
    let alive = true;
    api.get('/api/ai/sql-dashboards')
      .then(r => { if (alive) setSqlKeys(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (alive) setSqlKeys([]); });
    api.get('/api/ai/mr-sources')
      .then(r => { if (alive) setMrTargets(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (alive) setMrTargets([]); });
    return () => { alive = false; };
  }, []);

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]">
      {/* Picker bar */}
      <div className="flex-shrink-0 flex flex-wrap items-center gap-3 px-5 py-3 border-b border-slate-200 bg-white">
        <button
          onClick={toggleHistory}
          title={showHistory ? 'Sembunyikan riwayat' : 'Tampilkan riwayat'}
          className="flex-shrink-0 p-1.5 rounded-lg text-slate-500 hover:text-brand-600 hover:bg-slate-100 transition-colors">
          {showHistory
            ? <PanelLeftClose style={{ width: 16, height: 16 }} />
            : <PanelLeftOpen  style={{ width: 16, height: 16 }} />}
        </button>
        <span className="text-sm font-semibold text-slate-700">{t('aiw.pickerLabel')}</span>
        <div className="relative">
          <select
            value={sel.key}
            onChange={e => { setAutoPicked(false); setSel(targets.find(tgt => tgt.key === e.target.value) || targets[0]); }}
            className="appearance-none pl-3 pr-9 py-2 text-sm font-medium border border-slate-200 rounded-xl bg-white focus:border-brand-400 outline-none cursor-pointer">
            {targets.map(tgt => <option key={tgt.key || 'umum'} value={tgt.key}>{labelOf(tgt)}</option>)}
          </select>
          <ChevronDown style={{ width: 15, height: 15 }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        </div>
        {autoPicked && sel.key !== '' && (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-full bg-indigo-50 text-indigo-600 border border-indigo-200">
            🔀 {t('aiw.autoDetected')}
          </span>
        )}
        <span className="text-xs text-slate-400 hidden md:inline">
          {t('aiw.sessionNote')}
        </span>
      </div>

      {/* Riwayat (kiri, bisa dilipat) + chat (kanan) */}
      <div className="flex-1 min-h-0 flex flex-row">
        {showHistory && (
          <div className="flex-shrink-0 h-full min-h-0" style={{ width: HISTORY_W }}>
            <AIHistorySidebar
              dashboardName={dashboardName}
              activeSessionId={activeSessionId}
              refreshToken={historyTick}
              onPick={handlePickSession}
              onNewChat={handleNewChat}
              sessionHref={sid => `/dashboard/ai-workspace?session=${encodeURIComponent(sid)}`}
              lang={lang}
            />
          </div>
        )}
        <div className="flex flex-col h-full min-h-0" style={{ flex: 1, minWidth: 0 }}>
          {loading ? (
            <div className="flex items-center justify-center h-full gap-2 text-slate-400">
              <div className="w-4 h-4 border-2 border-brand-400 border-t-transparent rounded-full animate-spin" />
              <span className="text-xs">{t('aiw.loadingConfig')}</span>
            </div>
          ) : (
            <AIChat
              key={chatKey}
              currentPath={location.pathname}
              activeKey={sel.key || null}
              activeLabel={labelOf(sel)}
              storageLabel={storageLabelOf(sel)}
              initialConfig={config}
              onConfigChange={setConfig}
              onClose={() => navigate('/dashboard')}
              onAutoDetect={handleAutoDetect}
              resolveDashboardLabel={resolveDashboardLabel}
              suppressResetRef={suppressResetRef}
              isAutoPicked={autoPicked}
              resume={resume}
              onSessionActivity={handleSessionActivity}
            />
          )}
        </div>
      </div>
    </div>
  );
}
